import React, { useState } from 'react';
import { useAuth } from '../../store/auth.store';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const isAgaMember = user?.aga === 1;

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const triggerLogoutRequest = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
      <div className="text-center relative">
        {isAgaMember && (
          <div className="absolute top-0 right-0 lg:-right-20 animate-pulse">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3 flex items-center space-x-2">
              <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
              <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">AGA ELITE</span>
            </div>
          </div>
        )}
        <div className="inline-flex items-center justify-center h-24 w-24 rounded-3xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white font-black text-4xl shadow-2xl shadow-purple-600/20 mb-4 relative">
          <div className="absolute inset-0 bg-white/10 rounded-3xl animate-pulse"></div>
          {user?.fname?.[0]}
        </div>
        <h2 className="text-3xl font-black text-white">{user?.fname} {user?.lname}</h2>
        <div className="flex items-center justify-center space-x-2 mt-2">
           <Badge label={user?.is_admin === 1 ? 'ADMIN-CORE' : 'TRADER-AUTH'} variant="info" />
           <Badge label={user?.verified ? 'VERIFIED' : 'UNVERIFIED'} variant={user?.verified ? 'success' : 'warning'} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-slate-900/50 p-6 rounded-3xl border border-slate-800">
           <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Identification Data</p>
           <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-slate-800/50">
                <span className="text-slate-400 text-sm font-medium">Communication Channel</span>
                <span className="text-white font-bold text-sm">{user?.email}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-800/50">
                <span className="text-slate-400 text-sm font-medium">Network ID</span>
                <span className="text-white font-bold text-sm">#{user?.id}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-slate-800/50">
                <span className="text-slate-400 text-sm font-medium">AGA Membership</span>
                <span className={`font-black text-xs uppercase tracking-widest ${isAgaMember ? 'text-amber-500' : 'text-slate-600'}`}>
                  {isAgaMember ? 'ACTIVE MEMBER' : 'NOT ENROLLED'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-slate-400 text-sm font-medium">KYC Status</span>
                <Link to="/kyc" className="text-purple-500 font-bold text-sm hover:underline uppercase tracking-tighter">View Clearance Documents</Link>
              </div>
           </div>
        </div>

        <div className="bg-slate-900/50 p-6 rounded-3xl border border-slate-800 group hover:border-purple-500/20 transition-all">
           <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Security Preferences</p>
           <button className="w-full text-left py-3 text-slate-400 hover:text-white transition-colors text-sm font-medium flex justify-between items-center">
             <span>Update Security Key (Password)</span>
             <svg className="w-4 h-4 text-slate-500 group-hover:text-purple-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
           </button>
        </div>

        <button 
          onClick={triggerLogoutRequest}
          className="w-full bg-rose-500/5 p-6 rounded-3xl border border-rose-500/20 group hover:bg-rose-500/10 transition-all cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-rose-500/40"
        >
           <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mb-1">Session Protocol</p>
                <p className="text-white font-bold text-sm">Terminate Current Access</p>
              </div>
              <div className="w-10 h-10 bg-rose-500/10 rounded-xl flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-colors">
                 <svg className="w-5 h-5 text-rose-500 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                 </svg>
              </div>
           </div>
        </button>
      </div>

      {/* Custom Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl animate-in fade-in duration-300">
           <div className="bg-slate-900 border border-rose-500/20 w-full max-w-md p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden text-center">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-500/10 rounded-full blur-3xl"></div>
              
              <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-rose-500/20 shadow-inner">
                 <svg className="w-10 h-10 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                 </svg>
              </div>

              <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Protocol Termination</h3>
              <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
                Confirming this action will end your secure encrypted session and revoke current terminal access.
              </p>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleLogout} 
                  className="w-full py-5 bg-rose-600 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-xl shadow-rose-600/30 hover:bg-rose-500 transition-all active:scale-95"
                >
                  Confirm Disconnect
                </button>
                <button 
                  onClick={() => setShowLogoutModal(false)} 
                  className="w-full py-5 bg-slate-800 text-slate-400 font-black uppercase text-[10px] tracking-widest rounded-2xl hover:bg-slate-700 transition-all active:scale-95"
                >
                  Stay Connected
                </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default Profile;